import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 3px 5px 0px rgba(9, 30, 66, 0.20);
  margin-bottom: 16px;
`;

const InfoBox = styled(Box)`
  background-color: #f5f8ff;
  border-left: 4px solid #2196f3;
  padding: 12px 16px;
  border-radius: 4px;
  margin-top: 16px;
  display: flex;
  gap: 8px;
`;

function AutoRenewalSettings({ workspaceId }) {
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getSubscriptionSettings(workspaceId),
      });

      if (response.data) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error('Error fetching auto-renewal settings:', err);
      setError('Failed to load auto-renewal settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getActiveSubscription(workspaceId),
      });

      if (response.data) {
        setSubscriptionInfo(response.data);
      }
    } catch (err) {
      console.error('Error fetching subscription info:', err);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchSubscriptionInfo();
  }, [workspaceId]);

  const handleToggleAutoRenewal = async (event) => {
    const newValue = event.target.checked;

    setUpdating(true);
    setError(null);
    setSuccessMessage('');

    try {
      const response = await sendRequest({
        method: 'put',
        url: API.WORKSPACE.updateAutoRenewal(workspaceId),
        data: {
          autoRenewalEnabled: newValue,
        },
      });

      if (response.success) {
        setSettings({
          ...settings,
          autoRenewalEnabled: newValue,
        });
        setSuccessMessage(
          newValue
            ? 'Auto-renewal enabled. Your subscription will renew automatically.'
            : 'Auto-renewal disabled. You will need to manually renew your subscription.',
        );

        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Error updating auto-renewal:', err);
      setError('Failed to update auto-renewal setting. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const formatCardBrand = (brand) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const formatExpiryDate = (month, year) => {
    if (!month || !year) return '';
    return `${String(month).padStart(2, '0')}/${year}`;
  };

  const formatNextBillingDate = (date) => {
    if (!date) return 'Not available';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="warning">
        Unable to load auto-renewal settings. Please refresh the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Auto-Renewal Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <StyledCard>
        <CardContent>
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
          }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Automatic Renewal
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={settings.autoRenewalEnabled}
                  onChange={handleToggleAutoRenewal}
                  disabled={updating || !settings.hasPaymentMethod}
                  color="primary"
                />
              )}
              label={settings.autoRenewalEnabled ? 'Enabled' : 'Disabled'}
              labelPlacement="start"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {settings.autoRenewalEnabled
              ? 'Your subscription will automatically renew on the billing date using your saved payment method.'
              : 'Auto-renewal is disabled. You will need to manually renew your subscription before it expires.'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Payment Method Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Payment Method
            </Typography>
            {settings.hasPaymentMethod && settings.paymentMethod ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2">
                  {formatCardBrand(settings.paymentMethod.brand)}
                  {' '}
                  ending in
                  {settings.paymentMethod.last4}
                  {settings.paymentMethod.expMonth && settings.paymentMethod.expYear && (
                    <span style={{ color: '#666', marginLeft: '8px' }}>
                      Expires
                      {' '}
                      {formatExpiryDate(settings.paymentMethod.expMonth, settings.paymentMethod.expYear)}
                    </span>
                  )}
                </Typography>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No payment method saved. Complete a subscription payment to enable auto-renewal.
              </Alert>
            )}
          </Box>

          {/* Next Billing Date */}
          {subscriptionInfo?.expiryDate && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Next Billing Date
                </Typography>
                <Typography variant="body2">
                  {formatNextBillingDate(subscriptionInfo.expiryDate)}
                </Typography>
                {settings.autoRenewalEnabled && settings.hasPaymentMethod && (
                  <Chip
                    label="Will auto-renew"
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </>
          )}
        </CardContent>
      </StyledCard>

      {/* Informational Box */}
      <InfoBox>
        <InfoOutlinedIcon sx={{ color: '#2196f3', fontSize: '20px', mt: '2px' }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            How Auto-Renewal Works
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '13px', lineHeight: 1.5 }}>
            • Your subscription will automatically renew on the billing date
            <br />
            • The saved payment method will be charged for the current number of active users
            <br />
            • You can disable auto-renewal at any time before the next billing date
            <br />
            {'• If payment fails, you\'ll have until the expiry date to update your payment method'}
          </Typography>
        </Box>
      </InfoBox>
    </Box>
  );
}

AutoRenewalSettings.propTypes = {
  workspaceId: PropTypes.number.isRequired,
};

export default AutoRenewalSettings;
